import { SupportedLocation } from "./locationRegistry";
import { NicheJobIntent, getNicheIntentBySlug } from "./nicheIntentRegistry";
import { sanitizeDuplicateWords, getCategoryBaseName } from "./jobCategoryRegistry";

export interface ProgrammaticSeoMetadata {
  tier: 1 | 2 | 3;
  title: string;
  metaDescription: string;
  h1: string;
  canonicalUrl: string;
  breadcrumbLabel: string;
  structuredDataType: string;
}

const PRIMARY_DOMAIN = "https://getbiznearyou.com";

/**
 * Sanitizes and asserts Google character limit constraints.
 * Title: <= 60 chars
 * Description: <= 160 chars
 */
export function sanitizeMetaTitle(title: string, maxLength = 60): string {
  const clean = title.trim();
  if (clean.length <= maxLength) return clean;
  return clean.substring(0, maxLength - 3).trim() + "...";
}

export function sanitizeMetaDescription(desc: string, maxLength = 160): string {
  const clean = desc.trim();
  if (clean.length <= maxLength) return clean;
  return clean.substring(0, maxLength - 3).trim() + "...";
}

/**
 * TIER 1: STATE-LEVEL HUB PAGES
 * Target: Broad statewide keyword volume (e.g. "Mississippi jobs", "Mississippi employers directory")
 * Rule: Avoid hyper-local city words in metadata. Focus on "Statewide", "Comprehensive", "Directory".
 */
export function generateTier1StateMetadata(stateName: string, stateSlug: string, variant: "jobs" | "businesses-hiring" | "internships" = "jobs"): ProgrammaticSeoMetadata {
  const cleanState = stateName.trim();
  
  let rawTitle = `${cleanState} Local Jobs & Hiring | BizNearYou`;
  let rawDesc = `Browse verified employers, local business records, and active hiring signals across ${cleanState}. Discover regional job market trends on BizNearYou.`;
  let rawH1 = `${cleanState} Employers & Hiring Directory`;

  if (variant === "businesses-hiring") {
    rawTitle = `${cleanState} Local Businesses Hiring | BizNearYou`;
    rawDesc = `Explore commercial growth, medical expansion, and active business hiring signals across ${cleanState}. Connect with verified management on BizNearYou.`;
    rawH1 = `${cleanState} Businesses Displaying Hiring Signals`;
  } else if (variant === "internships") {
    rawTitle = `${cleanState} Student Internship Opportunities | BizNearYou`;
    rawDesc = `Discover local law firms, clinics, engineering practices, and businesses open to student internships in ${cleanState} on BizNearYou.`;
    rawH1 = `${cleanState} Student Internships & Early Career Roles`;
  }

  const route = `/${variant}/${stateSlug}`;

  return {
    tier: 1,
    title: sanitizeMetaTitle(rawTitle, 60),
    metaDescription: sanitizeMetaDescription(rawDesc, 160),
    h1: rawH1,
    canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
    breadcrumbLabel: cleanState,
    structuredDataType: "ItemList"
  };
}

/**
 * TIER 2: CITY-LEVEL PAGES
 * Target: Hyper-local city search intent (e.g. "Jobs in Jackson MS", "Jackson local employers")
 * Rule: Explicitly include City Name, State Abbreviation, County/Metro descriptors, and specific employer counts.
 */
export function generateTier2CityMetadata(
  loc: SupportedLocation, 
  variant: "jobs" | "businesses-hiring" | "internships" = "jobs"
): ProgrammaticSeoMetadata {
  const city = loc.cityName;
  const stateAbbr = loc.stateAbbreviation;
  const count = loc.businessCount || 350;
  const countyStr = loc.localHighlights?.metroName ? ` (${loc.localHighlights.metroName})` : "";

  let rawTitle = `Jobs in ${city} ${stateAbbr}: ${count}+ Local Employers | BizNearYou`;
  let rawDesc = `Discover ${count}+ local employers and active job opportunities in ${city}, ${stateAbbr}${countyStr}. Connect directly with verified business contacts on BizNearYou.`;
  let rawH1 = `Local Employers & Opportunities in ${city}, ${stateAbbr}`;

  if (variant === "businesses-hiring") {
    rawTitle = `Businesses Hiring in ${city} ${stateAbbr} | BizNearYou`;
    rawDesc = `Explore local businesses in ${city}, ${stateAbbr}${countyStr} displaying active hiring signals, team expansion, and review surges. Reach owners directly.`;
    rawH1 = `Businesses Displaying Hiring Signals in ${city}, ${stateAbbr}`;
  } else if (variant === "internships") {
    rawTitle = `Internships in ${city} ${stateAbbr} | BizNearYou`;
    rawDesc = `Find student internships and entry-level positions at local practices and businesses in ${city}, ${stateAbbr}${countyStr}. Connect with partners directly.`;
    rawH1 = `Student Internships & First Jobs in ${city}, ${stateAbbr}`;
  }

  const route = `/${variant}/${loc.citySlug}`;

  return {
    tier: 2,
    title: sanitizeMetaTitle(rawTitle, 60),
    metaDescription: sanitizeMetaDescription(rawDesc, 160),
    h1: rawH1,
    canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
    breadcrumbLabel: `${city}, ${stateAbbr}`,
    structuredDataType: "Place"
  };
}

/**
 * TIER 3: NICHE JOB INTENT PAGES
 * Target: High-converting intent searches (e.g. "Part-Time Jobs in Jackson MS", "Remote Jobs in Austin TX")
 * Rule: Hyper-local urgency, tokens [City], [State_Code], [Job_Type]
 */
export function generateTier3NicheMetadata(
  loc: SupportedLocation,
  niche: NicheJobIntent,
  variant: "jobs" | "businesses-hiring" | "internships" = "jobs"
): ProgrammaticSeoMetadata {
  const city = loc.cityName;
  const stateAbbr = loc.stateAbbreviation;

  const baseNicheName = getCategoryBaseName(niche.name);
  const rawTitle = sanitizeDuplicateWords(`${baseNicheName} Jobs in ${city} ${stateAbbr} | BizNearYou`);
  const rawDesc = sanitizeDuplicateWords(`Explore ${baseNicheName.toLowerCase()} job opportunities and local hiring employers in ${city}, ${stateAbbr}. Apply directly to verified businesses with BizNearYou.`);
  const rawH1 = sanitizeDuplicateWords(`${baseNicheName} Jobs in ${city}, ${stateAbbr}`);

  const route = `/jobs/${loc.citySlug}/${niche.slug}`;

  return {
    tier: 3,
    title: sanitizeMetaTitle(rawTitle, 60),
    metaDescription: sanitizeMetaDescription(rawDesc, 160),
    h1: rawH1,
    canonicalUrl: `${PRIMARY_DOMAIN}${route}`,
    breadcrumbLabel: `${niche.shortName} - ${city}`,
    structuredDataType: "ItemList"
  };
}

/**
 * INTERNAL LINKING ANCHOR TEXT BLUEPRINT
 * Generates structured internal links establishing a clean silo hierarchy:
 * Tier 1 State -> Tier 2 Cities -> Tier 3 Niche Intents
 */
export interface InternalLinkItem {
  label: string;
  path: string;
  anchorText: string;
  tier: 1 | 2 | 3;
}

export function getInternalLinkingBlueprint(
  currentLoc: SupportedLocation,
  allStateCities: SupportedLocation[],
  nicheIntents: NicheJobIntent[]
): {
  parentStateLink: InternalLinkItem;
  sisterCityLinks: InternalLinkItem[];
  nicheIntentLinks: InternalLinkItem[];
} {
  const stateSlug = currentLoc.stateName.toLowerCase().replace(/\s+/g, "-");
  
  // Tier 1 Link
  const parentStateLink: InternalLinkItem = {
    label: `${currentLoc.stateName} Employers Directory`,
    path: `/jobs/${stateSlug}`,
    anchorText: `Explore All ${currentLoc.stateName} Employers & Job Market Hub`,
    tier: 1
  };

  // Tier 2 Links
  const sisterCityLinks: InternalLinkItem[] = allStateCities
    .filter(c => c.citySlug !== currentLoc.citySlug)
    .slice(0, 8)
    .map(c => ({
      label: `${c.cityName}, ${c.stateAbbreviation}`,
      path: `/jobs/${c.citySlug}`,
      anchorText: `Jobs & Employers in ${c.cityName}, ${c.stateAbbreviation}`,
      tier: 2
    }));

  // Tier 3 Links
  const nicheIntentLinks: InternalLinkItem[] = nicheIntents.map(n => ({
    label: `${n.name} Jobs`,
    path: `/jobs/${currentLoc.citySlug}/${n.slug}`,
    anchorText: `${n.name} Jobs in ${currentLoc.cityName}, ${currentLoc.stateAbbreviation}`,
    tier: 3
  }));

  return {
    parentStateLink,
    sisterCityLinks,
    nicheIntentLinks
  };
}
